/**
 * invoiceLinePresence: the client mirror of hasInvoiceLine in
 * api/_shared/invoiceLine.ts, the one test of whether a stored summary
 * holds an invoice line. False for null, blank, or a summary that is only
 * a no-charge marker ("No charge", "NO CHARGE -", "(No charge)",
 * "NOCHARGE" and the rest). A "No charge ..." sentence is a line.
 *
 * It gates the browser's doors only: the NotesFeed band, draft sub-row
 * and Mark as ready, the two summary editors' Save, and the dev notes
 * mock. saysNoCharge, below, decides the no-charge-with-hours warning. It never renders a line: the line as the invoice prints it comes
 * from the server (invoice_line), and the no-charge prefix lives only
 * there. tests/ui/invoiceLineParity.test.ts proves this file and the
 * server module agree.
 */

/** A leading no-charge marker in any case and form. */
const MARKER_RE = /^[([]?\s*no[\s-]*charge\b/i;

/** What may follow a standalone marker: nothing, or a closing bracket,
 *  one of . , : ; ! or a hyphen, en dash or em dash. */
const STANDALONE_TAIL_RE = /^\s*(?:$|[)\].,:;!\-\u2013\u2014])/;

/** The run a standalone marker's tail sheds before its remainder. */
const TAIL_LEAD_RE = /^[\s)\].,:;!\-\u2013\u2014]+/;

/** Whether a stored summary holds an invoice line. Pure. */
export function hasInvoiceLine(summary: string | null): boolean {
  const text = (summary ?? '').trim();
  if (text === '') return false;
  const marker = MARKER_RE.exec(text);
  if (marker === null) return true;
  const tail = text.slice(marker[0].length);
  if (!STANDALONE_TAIL_RE.test(tail)) return true;
  return tail.replace(TAIL_LEAD_RE, '').trim() !== '';
}

/** Every "no charge" in any case and form, never inside a word
 *  ("Casino-charge") and never "No charges": the server's phrase
 *  (api/_shared/invoiceLine.ts PHRASE_RE). */
const NO_CHARGE_PHRASE_RE = /(?<![\p{L}\p{N}])no[\s-]*charge\b/iu;

/** Whether a stored summary says no charge anywhere. False for null and
 *  blank. It decides only the no-charge-with-hours warning. Pure. */
export function saysNoCharge(summary: string | null): boolean {
  return summary !== null && NO_CHARGE_PHRASE_RE.test(summary);
}

/** Whether a stored summary is typed in capitals: with every "no charge"
 *  and its first letter left out, no lower-case letter and at least four
 *  letters ("MD" or "No charge. PCP" are not). Names cannot be told from
 *  words in such a line, so it is never re-cased; the screens ask the
 *  person to retype it. False for null and blank. Pure. */
export function isInCapitals(summary: string | null): boolean {
  const probe = (summary ?? '').trim().replace(new RegExp(NO_CHARGE_PHRASE_RE.source, 'giu'), '').replace(/\p{L}/u, '');
  return !/\p{Ll}/u.test(probe) && (probe.match(/\p{L}/gu)?.length ?? 0) >= 4;
}
